import type { RequestContainer } from '@/api';
import {
  isRequestBlockCompleted,
  isRequestProgressCompleted,
} from '@/entities/request/lib/requestProgress';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Check, LockKeyhole, MessageSquare, Paperclip, Pin } from 'lucide-react';
import React from 'react';
import * as S from './styles';

// ─── Deadline formatter ──────────────────────────────────────────────────────

export type DeadlineColorVariant = 'default' | 'warning' | 'error';

export interface FormattedDeadline {
  text: string;
  color: DeadlineColorVariant;
}

/**
 * Converts a deadline date into display text and color based on proximity to today.
 * Future (>14d) → "May 5" | Coming Up (2–14d) → "In X days" | Imminent (1d) → "Tomorrow"
 * Today → "Today" | Past (1–7d) → "X days overdue" | Past (>7d) → "MMM D" (still error/red)
 */
export function formatDeadline(
  deadline: string | Date,
  referenceDate: string | Date = new Date(),
): FormattedDeadline {
  const d = dayjs(deadline).startOf('day');
  const ref = dayjs(referenceDate).startOf('day');

  if (!d.isValid()) return { text: '', color: 'default' };

  const daysDiff = d.diff(ref, 'day');

  if (daysDiff < 0) {
    const n = Math.abs(daysDiff);
    if (n > 7) {
      return { text: d.format('MMM D'), color: 'error' };
    }
    return { text: n === 1 ? '1 day overdue' : `${n} days overdue`, color: 'error' };
  }
  if (daysDiff === 0) return { text: 'Today', color: 'error' };
  if (daysDiff === 1) return { text: 'Tomorrow', color: 'error' };
  if (daysDiff <= 14) return { text: `In ${daysDiff} days`, color: 'default' };

  return { text: d.format('MMM D'), color: 'default' };
}

// ─── Progress circle ──────────────────────────────────────────────────────────

interface ProgressCircleProps {
  completed: number;
  total: number;
}

const ProgressCircle: React.FC<ProgressCircleProps> = ({ completed, total }) => {
  const percentage = total > 0 ? Math.min(completed / total, 1) : 0;

  const fillColor = percentage < 0.33 ? '#F17F16' : percentage < 0.66 ? '#4E76A0' : '#569720';
  const bgColor = percentage < 0.33 ? '#FCE5D0' : percentage < 0.66 ? '#E9F2FB' : '#EEF5E9';

  const cx = 7;
  const cy = 7;
  const r = 6;

  const buildArcPath = (): string => {
    const angle = percentage * 360;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const x1 = cx + r * Math.cos(toRad(-90));
    const y1 = cy + r * Math.sin(toRad(-90));
    const x2 = cx + r * Math.cos(toRad(-90 + angle));
    const y2 = cy + r * Math.sin(toRad(-90 + angle));
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${angle > 180 ? 1 : 0} 1 ${x2} ${y2} Z`;
  };

  return (
    <svg width={12} height={12} viewBox='0 0 14 14' style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill={bgColor} />
      {percentage >= 1 ? (
        <circle cx={cx} cy={cy} r={r} fill={fillColor} />
      ) : percentage > 0 ? (
        <path d={buildArcPath()} fill={fillColor} />
      ) : null}
    </svg>
  );
};

// ─── Props ────────────────────────────────────────────────────────────────────

export interface RequestItemProps {
  /** All request data – title, progress, deadline, counts, flags */
  data: RequestContainer;

  /** Custom class name */
  className?: string;

  /** Whether the item is currently selected */
  isActive?: boolean;

  /** Click handler for the entire card */
  onClick?: () => void;

  /** Click handler for the pin icon button */
  onPinClick?: (e: React.MouseEvent) => void;

  /** When false, the pin control is hidden (e.g. client users). Default true. */
  showPin?: boolean;

  /**
   * Whose Progress the card shows, and so when the request is completed and its deadline
   * goes: confirmed blocks for auditors, sent blocks for clients. Default true.
   */
  isAuditor?: boolean;

  /** Visual drag state used by request list drag-and-drop interactions. */
  isDragging?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const RequestItem: React.FC<RequestItemProps> = ({
  data,
  className,
  isActive,
  onClick,
  onPinClick,
  showPin = true,
  isAuditor = true,
  isDragging = false,
}) => {
  const { title, blocks, deadline, clientAccess, pinnedAt, commentAmount, attachmentAmount } = data;

  // ── Default variant ──────────────────────────────────────────────────────
  const progressCircle = blocks.length;
  const progressCompleted = blocks.filter((block) =>
    isRequestBlockCompleted(block, isAuditor),
  ).length;
  const progressLabel = progressCircle ? `${progressCompleted}/${progressCircle}` : undefined;

  // A completed request has nothing left to act on, so its card shows no deadline until
  // it leaves the completed state again; the request keeps the deadline itself.
  const formattedDeadline =
    deadline && !isRequestProgressCompleted(blocks, isAuditor) ? formatDeadline(deadline) : null;

  return (
    <S.RequestItemContainer
      isActive={isActive}
      isDragging={isDragging}
      onClick={onClick}
      className={className}
      data-request-id={data.id}
      data-testid={`request-item-${data.id}`}
    >
      <S.HeaderRow>
        <S.TitleContainer>
          <Typography variant='subtitle2' noWrap fontWeight={700} pr={1}>
            {title}
          </Typography>
        </S.TitleContainer>

        {showPin ? (
          <S.PinIconButton
            size='small'
            isPinned={!!pinnedAt}
            className='request-item-pin'
            data-testid={`request-pin-${data.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onPinClick?.(e);
            }}
          >
            <Pin size={12} />
          </S.PinIconButton>
        ) : null}
      </S.HeaderRow>

      <S.FooterRow>
        <S.FooterContent>
          {progressCircle > 0 && (
            <S.IconTextItem>
              {progressCompleted < progressCircle ? (
                <ProgressCircle completed={progressCompleted} total={progressCircle} />
              ) : (
                <S.CompletedCircle>
                  <Check size={10} strokeWidth={2.5} />
                </S.CompletedCircle>
              )}
              <Typography fontSize={11} lineHeight={1} variant='caption'>
                {progressLabel}
              </Typography>
            </S.IconTextItem>
          )}
          {formattedDeadline && (
            <S.DateText colorType={formattedDeadline.color}>{formattedDeadline.text}</S.DateText>
          )}
        </S.FooterContent>

        <S.FooterContent>
          {clientAccess === 'assigned_only' && (
            <S.IconTextItem>
              <LockKeyhole size={12} />
            </S.IconTextItem>
          )}
          {commentAmount ? (
            <S.IconTextItem>
              <span>{commentAmount}</span>
              <MessageSquare size={12} />
            </S.IconTextItem>
          ) : null}
          {attachmentAmount ? (
            <S.IconTextItem>
              <span>{attachmentAmount}</span>
              <Paperclip size={12} />
            </S.IconTextItem>
          ) : null}
        </S.FooterContent>
      </S.FooterRow>
    </S.RequestItemContainer>
  );
};

export default RequestItem;
